import React, { useEffect, useState } from "react";
import TermsModal from "../../components/TermsModal";
import {
  Box,
  Typography,
  Divider,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Breadcrumbs,
  Link,
  Alert,
  AlertTitle,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Fade,
  Tabs,
  Tab,
  Collapse,
  LinearProgress
} from "@mui/material";
import Header from "../../components/Header";
import { getUserInfo } from "../../api/auth";
import { useNavigate } from "react-router-dom";

// アイコン
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WorkIcon from '@mui/icons-material/Work';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import StorageIcon from '@mui/icons-material/Storage';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GroupIcon from '@mui/icons-material/Group';
import ScienceIcon from '@mui/icons-material/Science';

const PlanSettings = () => {
  const [user, setUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [showBenefits, setShowBenefits] = useState(false);
  const navigate = useNavigate();

  const sharedView = sessionStorage.getItem("sharedView") === "true";

  const fetchUser = async () => {
    try {
      setLoading(true);
      const userData = await getUserInfo();
      setUser(userData);
      setSelectedPlan(userData?.plan?.name?.toLowerCase() || "free");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const shared = sessionStorage.getItem("sharedView") === "true";
    if (shared) {
      alert("共有中アカウントではプラン設定画面にアクセスできません。");
      window.location.href = "/";
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getPriceIdFromPlan = (plan) => {
    switch (plan) {
      case "standard":
        // 本番環境用Price ID
        return "price_1RFb3aRogkGfn9cEPbROEo95";
        // 開発環境用Price ID（コメントアウト）
        // return "price_1RDLs2RogkGfn9cE1paM5gaS";
      default:
        return null;
    }
  };

  const handleCheckout = async () => {
    const priceId = getPriceIdFromPlan(selectedPlan);
    if (!priceId) {
      alert("無効なプランが選択されました");
      return;
    }

    try {
      const res = await fetch("/api/stripe/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + sessionStorage.getItem("token"),
        },
        body: JSON.stringify({ priceId }),
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("CheckoutセッションURLの取得に失敗しました");
      }
    } catch (err) {
      alert("セッション作成時にエラーが発生しました");
    }
  };

  const handleChange = (event) => {
    setSelectedPlan(event.target.value);
  };

  const handleSubmit = async () => {
    if (selectedPlan === "free") {
      const confirm = window.confirm(
        "無料プランに変更すると一部の顧客データが非表示になる可能性があります。\n" +
          "また、現在の無料体験期間が終了し、今後再契約される場合は即時課金となります。\n\n" +
          "本当に無料プランに戻しますか？"
      );
      if (!confirm) return;

      try {
        const res = await fetch("/api/stripe/cancel-subscription", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + sessionStorage.getItem("token"),
          },
        });

        if (res.ok) {
          alert("無料プランに変更しました");
          await fetchUser(); // ← 再取得して user.plan.id を確実に更新
        } else {
          alert("解約に失敗しました");
        }
      } catch (err) {
        alert("エラーが発生しました");
      }
      return;
    }

    if (!agreed) {
      setModalOpen(true);
      return;
    }

    await handleCheckout();
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress color="primary" />
        </Container>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            ユーザー情報の取得に失敗しました。再度お試しいただくか、管理者にお問い合わせください。
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/setting-Home')}
            sx={{ mt: 2 }}
          >
            設定ホームに戻る
          </Button>
        </Container>
      </>
    );
  }

  const { plan } = user;

  const getTrialStatus = () => {
    if (!user.hasUsedTrial) return "未使用";
    if (!user?.subscriptionStartAt) return "使用済";

    const start = new Date(user.subscriptionStartAt);
    const trialEnd = new Date(start);
    trialEnd.setDate(start.getDate() + 90);

    const now = new Date();
    const diff = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));

    if (diff <= 0) return "使用済";
    return `残り ${diff} 日`;
  };
  
  // 試用期間の進行状況を計算（0〜100のパーセント）
  const getTrialProgress = () => {
    if (!user.hasUsedTrial || !user?.subscriptionStartAt) return 100;
    
    const start = new Date(user.subscriptionStartAt);
    const trialEnd = new Date(start);
    trialEnd.setDate(start.getDate() + 90); // 90日間
    
    const now = new Date();
    const totalDays = 90;
    const daysUsed = Math.ceil((now - start) / (1000 * 60 * 60 * 24));
    
    return Math.min(100, Math.max(0, (daysUsed / totalDays) * 100));
  };

  // プラン比較データ
  const planComparison = [
      // 製品登録数の制限を追加
    {
      feature: "製品登録数",
      free: "1件まで",
      standard: "10件まで"
    },
    {
      feature: "顧客登録数",
      free: "100件まで",
      standard: "無制限"
    },
    {
      feature: "顧客あたりの履歴",
      free: "10件まで",
      standard: "無制限"
    },
    {
      feature: "グループ作成",
      free: "10グループまで",
      standard: "30グループまで"
    },
    {
      feature: "共有ユーザー数",
      free: "5人まで",
      standard: "10人まで"
    },
    {
      feature: "無料トライアル",
      free: "なし",
      standard: "90日間"
    },
    {
      feature: "月額料金（税込）",
      free: "¥0",
      standard: "¥3,300"
    }
  ];


  const trialStatus = getTrialStatus();
  const isTrialActive = trialStatus.includes('残り');
  const trialProgress = getTrialProgress();

  return (
    <>
      <Header />
      <Container 
  maxWidth="md" 
  sx={{ 
    mt: 4, 
    mb: 6, 
    px: { xs: 2, sm: 3 },
    // PWAボトムナビゲーション対応：安全マージンを確保
    pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
    // 最小高さを確保してスクロール可能にする
    minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
  }}
>
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/setting-Home')}
            style={{ cursor: 'pointer' }}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            設定
          </Link>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <WorkIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            プラン管理
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                プラン管理
              </Typography>
              <Typography color="text.secondary">
                利用プランの確認と変更ができます
              </Typography>
            </Box>

            {/* タブナビゲーション */}
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                variant="fullWidth"
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}
              >
                <Tab 
                  label="現在のプラン" 
                  icon={<InfoIcon />} 
                  iconPosition="start"
                  sx={{ py: 2 }}
                />
                <Tab 
                  label="プラン変更" 
                  icon={<CompareArrowsIcon />} 
                  iconPosition="start"
                  sx={{ py: 2 }}
                />
                <Tab 
                  label="お支払い" 
                  icon={<PaymentIcon />} 
                  iconPosition="start"
                  sx={{ py: 2 }}
                />
              </Tabs>

              {/* 現在のプラン情報タブ */}
              {activeTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* 左側: プラン概要 */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          bgcolor: 'primary.light', 
                          color: 'primary.dark',
                          borderRadius: 2,
                          height: '100%'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <LocalOfferIcon />
                            <Typography variant="h6" fontWeight="medium">
                              {plan.name}プラン
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ borderColor: 'primary.main', opacity: 0.2, my: 2 }} />
                          
                          <List disablePadding>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                              <ListItemText 
                                primary={
                                  <Typography variant="body2" fontWeight="medium">
                                    月額料金（税込）
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 'bold' }}>
                                    ¥{plan.price.toLocaleString()}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                              <ListItemText 
                                primary={
                                  <Typography variant="body2" fontWeight="medium">
                                    無料トライアル
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body1" sx={{ color: 'primary.dark' }}>
                                    {trialStatus}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            
                            {isTrialActive && (
                              <Box sx={{ mt: 1, mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption">0日</Typography>
                                  <Typography variant="caption">90日</Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={trialProgress}
                                  sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: 'primary.main'
                                    }
                                  }}
                                />
                              </Box>
                            )}
                            
                            {user?.isInternal && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                自社アカウントのため、無料トライアルは適用されません。
                              </Alert>
                            )}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* 右側: 利用制限とメリット */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          border: 1, 
                          borderColor: 'divider',
                          borderRadius: 2,
                          height: '100%'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <StorageIcon color="primary" />
                            <Typography variant="h6" fontWeight="medium">
                              利用制限
                            </Typography>
                          </Box>
                          
                          <List disablePadding>
                          
                          
                                                      <ListItem disablePadding>
  <ListItemText 
    primary={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceIcon color="primary" fontSize="small" />
        <Typography variant="body2" fontWeight="medium">
          製品登録上限
        </Typography>
      </Box>
    }
    secondary={
      <Typography variant="body1" fontWeight="medium" color="text.primary">
        {plan.name === "Standard" ? "10 件" : "1 件"}
      </Typography>
    }
  />
</ListItem>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PeopleIcon color="primary" fontSize="small" />
                                    <Typography variant="body2" fontWeight="medium">
                                      顧客登録上限
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="body1" fontWeight="medium" color="text.primary">
                                    {plan.name === "Standard" ? "無制限" : `${plan.max_clients} 件`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            
                            <ListItem disablePadding sx={{ mb: 1 }}>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <HistoryIcon color="primary" fontSize="small" />
                                    <Typography variant="body2" fontWeight="medium">
                                      顧客あたりの履歴保存上限
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="body1" fontWeight="medium" color="text.primary">
                                    {plan.name === "Standard" ? "無制限" : `${plan.max_history} 件`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            
                            <ListItem disablePadding sx={{ mb: 1 }}>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GroupIcon color="primary" fontSize="small" />
                                    <Typography variant="body2" fontWeight="medium">
                                      グループ登録上限
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="body1" fontWeight="medium" color="text.primary">
                                    {plan.max_groups ?? "-"} 件
                                  </Typography>
                                }
                              />
                            </ListItem>
                            
                            <ListItem disablePadding>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PeopleIcon color="primary" fontSize="small" />
                                    <Typography variant="body2" fontWeight="medium">
                                      共有許可上限
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="body1" fontWeight="medium" color="text.primary">
                                    {plan.max_shared_users ?? "-"} 人
                                  </Typography>
                                }
                              />
                            </ListItem>
                            
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 3 }}
                    onClick={() => setActiveTab(1)}
                  >
                    プランを変更する
                  </Button>
                </Box>
              )}

              {/* プラン変更タブ */}
              {activeTab === 1 && (
                <Box sx={{ p: 3 }}>
                  {/* プラン比較表 */}
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      border: 1, 
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 3
                    }}
                  >
                    <Box sx={{ bgcolor: 'grey.100', p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        プラン比較
                      </Typography>
                    </Box>
                    <Divider />
                    
                    <Box sx={{ p: 0 }}>
                      <Grid container sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Grid item xs={4} sx={{ p: 2, borderRight: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" fontWeight="medium">
                            機能
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ p: 2, borderRight: 1, borderColor: 'divider', bgcolor: selectedPlan === 'free' ? 'primary.50' : 'transparent' }}>
                          <Typography variant="subtitle2" fontWeight="medium">
                            Freeプラン
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ p: 2, bgcolor: selectedPlan === 'standard' ? 'primary.50' : 'transparent' }}>
                          <Typography variant="subtitle2" fontWeight="medium">
                            Standardプラン
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      {planComparison.map((item, index) => (
                        <Grid container key={index} sx={{ borderBottom: index < planComparison.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                          <Grid item xs={4} sx={{ p: 2, borderRight: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                            <Typography variant="body2">
                              {item.feature}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ p: 2, borderRight: 1, borderColor: 'divider', bgcolor: selectedPlan === 'free' ? 'primary.50' : 'transparent' }}>
                            <Typography variant="body2">
                              {item.free}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ p: 2, bgcolor: selectedPlan === 'standard' ? 'primary.50' : 'transparent' }}>
                            <Typography variant="body2" fontWeight={item.feature.includes('トライアル') || item.feature.includes('料金') ? 'bold' : 'normal'}>
                              {item.standard}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  </Paper>
                  
                  {/* プラン選択 */}
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    プランを選択：
                  </Typography>
                  <RadioGroup 
                    value={selectedPlan} 
                    onChange={handleChange}
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: 2
                    }}
                  >
                    <Paper 
                      elevation={selectedPlan === 'free' ? 3 : 1}
                      sx={{ 
                        flex: 1, 
                        borderRadius: 2,
                        p: 2,
                        border: 1,
                        borderColor: selectedPlan === 'free' ? 'primary.main' : 'divider'
                      }}
                    >
                      <FormControlLabel 
                        value="free" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Freeプラン
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ¥0 / 月・税込
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%' }}
                      />
                    </Paper>
                    
                    <Paper 
                      elevation={selectedPlan === 'standard' ? 3 : 1}
                      sx={{ 
                        flex: 1, 
                        borderRadius: 2,
                        p: 2,
                        border: 1,
                        borderColor: selectedPlan === 'standard' ? 'primary.main' : 'divider'
                      }}
                    >
                      <FormControlLabel 
                        value="standard" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Standardプラン
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ¥3,300 / 月・税込
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%' }}
                      />
                    </Paper>
                  </RadioGroup>

                  {!user.isInternal && (
                    <Alert severity="info" sx={{ mt: 3 }}>
                      <AlertTitle>無料トライアルについて</AlertTitle>
                      初回登録から3ヶ月間は無料でStandardプランをお試しいただけます。
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    onClick={handleSubmit}
                  >
                    このプランに変更する
                  </Button>
                </Box>
              )}

              {/* お支払い情報タブ */}
              {activeTab === 2 && (
                <Box sx={{ p: 3 }}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      borderRadius: 2,
                      p: 3,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      bgcolor: 'primary.50'
                    }}
                  >
                    <CreditCardIcon color="primary" sx={{ mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                        お支払い情報
                      </Typography>
                      <Typography variant="body2">
                        Standardプランをご契約いただいている場合、お支払い情報の確認や変更ができます。
                      </Typography>
                    </Box>
                  </Paper>
                  
                  {!(user.plan?.id === 1 || !user.stripeCustomerId) ? (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2 }}
                      startIcon={<CreditCardIcon />}
                      onClick={async () => {
                        try {
                          const res = await fetch("/api/stripe/customer-portal", {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: "Bearer " + sessionStorage.getItem("token"),
                            },
                          });
                          const data = await res.json();
                          if (data.url) {
                            window.location.href = data.url;
                          } else {
                            alert("ポータルURLの取得に失敗しました");
                          }
                        } catch {
                          alert("エラーが発生しました");
                        }
                      }}
                    >
                      お支払い情報の確認・変更はこちら
                    </Button>
                  ) : (
                    <Alert severity="info">
                      <AlertTitle>お支払い情報はまだありません</AlertTitle>
                      Standardプランにご加入いただくと、お支払い情報の確認・変更ができるようになります。
                    </Alert>
                  )}
                </Box>
              )}
            </Paper>
            
            {/* アクションボタン */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center',pb: { xs: 2, sm: 0 } }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/setting-Home')}
                size="large"
                sx={{ minWidth: 200,
                minHeight: { xs: 48, sm: 40 }
                 }}
              >
                設定ホームに戻る
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* 利用規約同意モーダル */}
      <TermsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAgree={async () => {
          setAgreed(true);
          setModalOpen(false);
          await handleCheckout();
        }}
      />
    </>
  );
};

export default PlanSettings;